import { formatDistanceToNow } from "date-fns";
import React, { useEffect, useState } from "react";
import { getNotifications } from "../apis/notifications/get.notifications";
import { Notification, NotificationType } from "../types/notification";
import Icon from "./Icon";

interface NotificationListProps {
  limit?: number;
}

const NotificationList: React.FC<NotificationListProps> = ({ limit = 20 }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        await getNotifications(
          {
            onCompleted: ({ data }) => {
              setNotifications(data.notifications);
              setLoading(false);
            },
            onError: (err) => {
              console.error("Notifications API error:", err);
              setError(err || "Failed to fetch notifications");
              setLoading(false);
            },
            query: {
              limit,
            },
          },
          { bypassCache: true }
        );
      } catch (err) {
        setError("Failed to fetch notifications");
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [limit]);

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.NEW_RSVP:
        return <Icon name="users" className="w-5 h-5 text-blue-500" />;
      case NotificationType.ASO_EBI_SALE:
        return <Icon name="coins" className="w-5 h-5 text-green-500" />;
      case NotificationType.NEW_WELL_WISH:
        return <Icon name="wellWishes" className="w-5 h-5 text-pink-500" />;
      case NotificationType.PLANNER_TEAM:
        return <Icon name="users" className="w-5 h-5 text-amber-700" />;
      case NotificationType.WEDDING_PLAN:
        return <Icon name="envelope" className="w-5 h-5 text-emerald-700" />;
      default:
        return <Icon name="envelope" className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationBgColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.NEW_RSVP:
        return "bg-blue-50 border-blue-200";
      case NotificationType.ASO_EBI_SALE:
        return "bg-green-50 border-green-200";
      case NotificationType.NEW_WELL_WISH:
        return "bg-pink-50 border-pink-200";
      case NotificationType.PLANNER_TEAM:
        return "bg-amber-50 border-amber-200";
      case NotificationType.WEDDING_PLAN:
        return "bg-emerald-50 border-emerald-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading notifications...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center">
          <Icon name="warning" className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="text-center p-8">
        <Icon
          name="envelope"
          className="w-12 h-12 text-gray-400 mx-auto mb-4"
        />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No notifications
        </h3>
        <p className="text-gray-500">
          You're all caught up! No new notifications to show.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {notifications.map((notification) => (
        <div
          key={notification._id}
          className={`p-4 rounded-lg border transition-all hover:shadow-md ${getNotificationBgColor(
            notification.type
          )}`}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1">
              {getNotificationIcon(notification.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900 truncate">
                  {notification.title}
                </h4>
                <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                  {formatDistanceToNow(new Date(notification.createdAt), {
                    addSuffix: true,
                  })}
                </span>
              </div>
              <p className="text-sm text-gray-700 mt-1 line-clamp-2">
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationList;
